import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './Trainers.css';
import { API_URL } from '../utils/api';

import { useLanguage } from '../contexts/LanguageContext';

function Trainers() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [trainers, setTrainers] = useState([]);
  const [filteredTrainers, setFilteredTrainers] = useState([]);
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || '');
  const [selectedCity, setSelectedCity] = useState('');
  const [searchPincode, setSearchPincode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [noTrainerModal, setNoTrainerModal] = useState(false);
  const [contactPerson, setContactPerson] = useState(null);
  const [profileModal, setProfileModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load trainers from API
  const loadTrainersData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/trainers`); // Use axios directly or apiCall
      const trainersData = response.data;
      setTrainers(trainersData);
      setFilteredTrainers(trainersData);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      // Fallback to empty if fails
      setTrainers([]);
      setFilteredTrainers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrainersData();
  }, []);

  // Function to perform search
  const performSearch = () => {
    let filtered = trainers;

    if (selectedState) {
      filtered = filtered.filter(t => t.state?.toLowerCase().includes(selectedState.toLowerCase()));
    }

    if (selectedCity) {
      filtered = filtered.filter(t => t.city?.toLowerCase().includes(selectedCity.toLowerCase()));
    }

    if (searchPincode) {
      filtered = filtered.filter(t => t.pincode?.includes(searchPincode));
    }

    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.specialization?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTrainers(filtered);
  };

  // Get unique states for dropdown
  const uniqueStates = [...new Set(trainers.filter(t => t.state).map(t => t.state))].sort();

  // Get unique cities based on selected state
  const uniqueCities = selectedState
    ? [...new Set(trainers.filter(t => t.state === selectedState && t.city).map(t => t.city))].sort()
    : [];

  const handleViewProfile = (trainer) => {
    setSelectedTrainer(trainer);
    setProfileModal(true);
  };

  const closeProfileModal = () => {
    setProfileModal(false);
    setSelectedTrainer(null);
  };

  const handleNoTrainerFound = () => {
    setContactPerson({
      name: t('trainers.no_results.support_name'),
      email: 'support@yogakaregaindia.com',
      phone: '+91-7078950111',
      message: t('trainers.no_results.support_msg')
    });
    setNoTrainerModal(true);
  };

  const closeModal = () => {
    setNoTrainerModal(false);
  };

  return (
    <div className="trainers-page">
      {/* Hero Section */}
      <section className="trainers-hero">
        <div className="container">
          <div className="hero-content" data-aos="fade-up">
            <div className="hero-content" data-aos="fade-up">
              <h1>{t('trainers.hero.title')}</h1>
              <p>{t('trainers.hero.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="search-section">
        <div className="container">
          <div className="search-container" data-aos="fade-up">
            <h2>{t('trainers.search.title')}</h2>

            <div className="search-bar">
              <input
                type="text"
                placeholder={t('trainers.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && performSearch()}
                className="search-input"
              />
              <button className="search-button" onClick={performSearch}>
                <span className="search-icon">🔍</span>
                <span className="search-text">{t('trainers.search.btn')}</span>
              </button>
            </div>

            <div className="filter-grid">
              <div className="filter-group">
                <label htmlFor="state-select">{t('trainers.search.state_label')}</label>
                <select
                  id="state-select"
                  value={selectedState}
                  onChange={(e) => {
                    setSelectedState(e.target.value);
                    setSelectedCity('');
                  }}
                  className="filter-select"
                >
                  <option value="">{t('trainers.search.all_states')}</option>
                  {uniqueStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="city-select">{t('trainers.search.city_label')}</label>
                <select
                  id="city-select"
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  disabled={!selectedState}
                  className="filter-select"
                >
                  <option value="">{t('trainers.search.all_cities')}</option>
                  {uniqueCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="pincode-input">{t('trainers.search.pincode_label')}</label>
                <input
                  id="pincode-input"
                  type="text"
                  placeholder={t('trainers.search.pincode_placeholder')}
                  value={searchPincode}
                  onChange={(e) => setSearchPincode(e.target.value)}
                  className="filter-input"
                  maxLength="6"
                />
              </div>

              <div className="filter-buttons">
                <button
                  className="btn btn-primary"
                  onClick={performSearch}
                >
                  🔍 {t('trainers.search.btn')}
                </button>

                <button
                  className="btn btn-info"
                  onClick={() => {
                    setSelectedState('');
                    setSelectedCity('');
                    setSearchPincode('');
                    setSearchTerm('');
                    setFilteredTrainers(trainers);
                  }}
                >
                  {t('trainers.search.clear_filters')}
                </button>
              </div>
            </div>

            {filteredTrainers.length > 0 && (
              <div className="results-info">
                {t('trainers.search.found_prefix')} <strong>{filteredTrainers.length}</strong> {t('trainers.search.found_suffix')}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="trainers-results">
        <div className="container">
          {loading ? (
            <div className="loading-spinner">Loading trainers...</div>
          ) : filteredTrainers.length > 0 ? (
            <div className="trainers-table-container">
              <table className="trainers-table">
                <thead>
                  <tr>
                    <th>{t('trainers.table.name')}</th>
                    <th>{t('trainers.table.spec')}</th>
                    <th>{t('trainers.table.city')}</th>
                    <th>{t('trainers.table.state')}</th>
                    <th>{t('trainers.table.pincode')}</th>
                    <th>{t('trainers.table.exp')}</th>
                    <th>{t('trainers.table.phone')}</th>
                    <th>{t('trainers.table.email')}</th>
                    <th>{t('trainers.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrainers.map((trainer, index) => (
                    <tr
                      key={trainer._id || trainer.id}
                      className={`trainer-row ${trainer.featured ? 'featured' : ''}`}
                      data-aos="fade-up"
                      data-aos-delay={index * 50}
                    >
                      <td>
                        <div className="trainer-name-cell">
                          <div className="trainer-avatar-small">
                            {trainer.name.charAt(0)}{trainer.name.split(' ')[1]?.charAt(0) || ''}
                          </div>
                          <div>
                            <strong>{trainer.name}</strong>
                          </div>
                        </div>
                      </td>
                      <td>{trainer.specialization}</td>
                      <td>{trainer.city}</td>
                      <td>{trainer.state}</td>
                      <td>{trainer.pincode}</td>
                      <td>{trainer.experience}</td>
                      <td>
                        <a href={`tel:${trainer.phone}`} className="contact-link-table">
                          📞 {trainer.phone}
                        </a>
                      </td>
                      <td>
                        <a href={`mailto:${trainer.email}`} className="contact-link-table">
                          📧 {trainer.email}
                        </a>
                      </td>
                      <td>
                        <div className="action-buttons-table">
                          <button className="btn btn-sm btn-primary" onClick={() => handleViewProfile(trainer)}>
                            {t('trainers.table.profile_btn')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <div className="no-results-icon">🧘‍♀️</div>
              <h3>{t('trainers.no_results.title')}</h3>
              <p>
                {t('trainers.no_results.desc')}
              </p>
              <button className="btn btn-primary" onClick={handleNoTrainerFound}>
                {t('trainers.no_results.contact_btn')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Trainer Profile Modal */}
      {profileModal && selectedTrainer && (
        <div className="modal-overlay" onClick={closeProfileModal}>
          <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('trainers.profile.title')}</h2>
              <button className="modal-close" onClick={closeProfileModal}>✕</button>
            </div>

            <div className="modal-body">
              <div className="profile-container">
                {/* Avatar */}
                <div className="profile-avatar-large">
                  {selectedTrainer.photo ? (
                    <img src={selectedTrainer.photo} alt={selectedTrainer.name} className="profile-photo-img" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  ) : (
                    <span>{selectedTrainer.name.charAt(0)}{selectedTrainer.name.split(' ')[1]?.charAt(0) || ''}</span>
                  )}
                </div>

                {/* Basic Info */}
                <div className="profile-info-section">
                  <h2 className="profile-name">{selectedTrainer.name}</h2>
                  <p className="profile-spec">{selectedTrainer.specialization}</p>
                  {selectedTrainer.featured && <span className="badge-featured">⭐ Featured Trainer</span>}
                </div>

                {/* Experience */}
                <div className="profile-card">
                  <h3>Experience</h3>
                  <p className="profile-value">{selectedTrainer.experience}</p>
                </div>

                {/* Location */}
                <div className="profile-card">
                  <h3>Location</h3>
                  <div className="profile-location">
                    <p><strong>City:</strong> {selectedTrainer.city}</p>
                    <p><strong>State:</strong> {selectedTrainer.state}</p>
                    <p><strong>Pin Code:</strong> {selectedTrainer.pincode}</p>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="profile-card">
                  <h3>{t('trainers.profile.contact_details')}</h3>
                  <div className="profile-contact-items">
                    <div className="profile-contact-item">
                      <span className="icon">📞</span>
                      <div>
                        <p className="label">{t('trainers.profile.phone')}</p>
                        <a href={`tel:${selectedTrainer.phone}`} className="link">
                          {selectedTrainer.phone}
                        </a>
                      </div>
                    </div>
                    <div className="profile-contact-item">
                      <span className="icon">📧</span>
                      <div>
                        <p className="label">{t('trainers.profile.email')}</p>
                        <a href={`mailto:${selectedTrainer.email}`} className="link">
                          {selectedTrainer.email}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="profile-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => window.location.href = `tel:${selectedTrainer.phone}`}
                  >
                    {t('trainers.profile.call_btn')}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => window.location.href = `mailto:${selectedTrainer.email}`}
                  >
                    {t('trainers.profile.email_btn')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Trainer Modal */}
      {noTrainerModal && contactPerson && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('trainers.no_results.modal_title')}</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body">
              <div className="contact-box">
                <h3>{contactPerson.name}</h3>
                <p className="contact-message">{contactPerson.message}</p>

                <div className="contact-details">
                  <div className="contact-detail">
                    <span className="icon">📧</span>
                    <div>
                      <p className="label">{t('trainers.table.email')}</p>
                      <a href={`mailto:${contactPerson.email}`} className="link">
                        {contactPerson.email}
                      </a>
                    </div>
                  </div>

                  <div className="contact-detail">
                    <span className="icon">📞</span>
                    <div>
                      <p className="label">{t('trainers.table.phone')}</p>
                      <a href={`tel:${contactPerson.phone}`} className="link">
                        {contactPerson.phone}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={() => window.location.href = `mailto:${contactPerson.email}`}>
                    {t('trainers.profile.email_btn')}
                  </button>
                  <button className="btn btn-secondary" onClick={() => window.location.href = `tel:${contactPerson.phone}`}>
                    {t('trainers.profile.call_btn')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Trainers;
